<template>
    <v-card
        class="elevation-0 mb-4"
        width="100%"
        max-width="500px"
        style="margin: 0 auto;"
     >
        <loading-overlay :loading="loading"></loading-overlay>

        <template v-if="!loading && postersBeneficiary">
            <template v-if="!showCarousel">
                <v-card-text>
                    <div class="px-6 pb-6 pt-0">
                        <v-row class="text-center">
                            <v-col>
                                <p class="font-weight-bold title">YOU ARE TO SEND</p>
                                <div class="d-sm-flex d-block justify-center align-center">
                                    <p class="my-0 mr-3 red--text flashing-text" color="red" style="font-size: 45px">
                                        {{getCurrencySymbol(match.matcher_currency)}}{{totalAmount}}
                                    </p>
                                    <v-btn @click="copyToClipboard(totalAmount)" class="mt-0 mt-lg-0">
                                        Copy <v-icon>mdi-content-copy</v-icon>
                                    </v-btn>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Payment Method -->
                        <v-row class="text-center justify-center" v-if="postersBeneficiary">
                            <v-col>
                                <p class="overline my-0">With</p>
                                <v-img
                                    class="mx-auto my-2"
                                    :src="payWithLogo"
                                    tile
                                    :width="isZelleOrInterac ? 100 : 175"
                                    contain
                                ></v-img>
                            </v-col>
                        </v-row>
                    </div>

                    <v-sheet
                        tile
                        :color="isZelleOrInterac ? '#ffffff' : '#f9f9f9'"
                        class="py-6 px-4 grey--text text--darken-3 mx-3 mx-lg-6 mb-6 rounded-lg"
                        :class="[{ 'pt-3 mb-12': isZelleOrInterac }]"
                        v-if="match"
                    >
                        <v-row v-if="postersBeneficiary" class="position-relative">
                            <component :is="paymentComponent" :recipientInfo="recipientInfo"></component>
                        </v-row>
                    </v-sheet>

                    <v-alert type="warning" class="body-2 mb-0 mx-3 mx-lg-0 px-6" text dense>
                        Please note that only complete payments will be accepted.
                        Partial and incomplete payments would not be acknowledged as
                        received and are unacceptable!
                    </v-alert>
                </v-card-text>

                <div class="mx-6 mt-6">
                    <v-switch
                        v-model="showTransferProcessInstructions"
                        color="primary"
                        class="mt-0"
                        style="height: 20px"
                        label="Show Instructions"
                    />
                </div>

                <v-card-actions v-if="postersBeneficiary" class="pa-3 mt-3">
                    <v-btn
                        class="text-capitalize px-6 cancel"
                        text
                        color="red darken-2"
                        large
                        @click="$emit('close')"
                    >
                        Close
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        v-if="postersBeneficiary.isPIS"
                        class="text-capitalize px-10 rounded-lg"
                        color="primary"
                        dark
                        depressed
                        large
                        @click="pay"
                    >
                        Pay
                    </v-btn>
                </v-card-actions>
            </template>
            <transfer-guide
                v-else
                @proceed="$emit('pay', recipientInfo)"
                :order="match"
                :postersBeneficiary="postersBeneficiary"
                :totalSlides="4"
            />
        </template>

        <div class="text-center pa-5" v-else>
            <v-progress-circular
                indeterminate
                color="primary"
                class="ml-2"
            />
        </div>
    </v-card>
</template>

<script>
    let currency = require('../../modules/currency.js');

    import zelle_payment from '../match-confirmed-reveal-type-zelle-payment';
    import interac_payment from '../match-confirmed-reveal-type-interac-payment';
    import faster_payment from '../match-confirmed-reveal-type-faster-payment';
    import sepa_payment from '../match-confirmed-reveal-type-sepa-payment';
    import TransferGuide from '../transfers/components/transfer-guide.vue';

    export default {
        props: {
            match: Object,
            postersBeneficiary: Object,
        },
        components: {
            zelle_payment: zelle_payment,
            interac_payment: interac_payment,
            faster_payment: faster_payment,
            sepa_payment: sepa_payment,
            TransferGuide,
        },
        data() {
            return {
                loading: true,
                showCarousel: false,
            }
        },
        created() {
            this.loading = true;
            Vue.backendApi().order().getPaymentWindow()
                .then((response) => {
                    this.loading = false;
                    const paymentWindow = response.data.value;
                    this.setClock(this.match.confirmed_at, paymentWindow)
                })
                .catch((err) => {
                    this.loading = false;
                    this.$swal({
                        type: 'error',
                        title: 'Error loading payment window',
                        text: err
                    });
                });
        },
        mounted() {
            this.$store.commit('pageHeaderClass', 'rateAlert');
            this.$store.commit('pageHeaderTitle', 'Make Payment');
            this.$store.commit('mobileMenuIsVisible', true);
            this.$store.commit('closeButtonIsVisible', false);
            this.$store.commit('hideLogoPart', false);
            this.loading=false;
        },
        methods: {
            getCurrencySymbol(currency_code) {
                return currency.getCurrencySymbol(currency_code);
            },
            setClock(start, lengthOfPaymentWindow) {
                let endDate = Vue.moment(start).add(lengthOfPaymentWindow*60, 'seconds');
                let timeRemaining = endDate.diff(Vue.moment(), 'seconds');
                timeRemaining = timeRemaining > 0 ? timeRemaining : 0;
                this.$emit('startTimer', timeRemaining);
            },
            pay() {
                if(!this.showTransferProcessInstructions) {
                    this.$emit('pay', this.recipientInfo);
                    return;
                }

                this.showCarousel = true;
            }
        },
        computed: getComputed(),
    };

    function getComputed() {
        return {
            totalAmount,
            payWithLogo,
            paymentComponent,
            isZelleOrInterac,
            recipientInfo,
            posterBeneficiaryFirstName,
            showTransferProcessInstructions: {
                get() {
                    return !this.$store.state.hideTransferInstructions;
                },
                set(value) {
                    this.$store.dispatch('updateTransferInstructions', !value);
                },
            },
        };

        function posterBeneficiaryFirstName() {
            const [first_name] = this.postersBeneficiary.full_name.split(' ');
            return first_name;
        }

        function isZelleOrInterac() {
            if (!this.postersBeneficiary) {
                return null;
            }

            const { deliveryMethod } = this.postersBeneficiary;
            const { method } = deliveryMethod;

            return deliveryMethod.method === 'ZELLE' || deliveryMethod.method === 'INTERAC';
        }

        function totalAmount() {
            const {
                matcher_fee: kaoshiFee,
                matcher_amount: matcherAmount,
            } = this.match;

            const totalAmount = parseFloat(kaoshiFee) + parseFloat(matcherAmount);

            return totalAmount.toFixed(2);
        }

        function payWithLogo() {
            if (!this.postersBeneficiary) {
                return null;
            }

            const { deliveryMethod } = this.postersBeneficiary;
            const { method } = deliveryMethod;

            const imageName = `${method.toLowerCase()}_payment`;
            return this.$_getPaymentLogoPath(this.paymentComponent);
        }

        function paymentComponent() {
            if (!this.postersBeneficiary) {
                return null;
            }

            const { deliveryMethod } = this.postersBeneficiary;
            const { provider, method } = deliveryMethod;

            return `${method.toLowerCase()}_payment`;
        }

        function recipientInfo() {
            if(!this.postersBeneficiary) {
                return {};
            }

            const { deliveryMethod } = this.postersBeneficiary;
            const {
                provider,
                method,
                institution_name: institutionName,
            } = deliveryMethod;
            let { account } = deliveryMethod;

            if (typeof account == "string") {
                try {
                    account = JSON.parse(account)
                } catch (err) {
                    console.log("could not parse poster beneficiary account")
                }
            }

            if(method === 'ZELLE') {
                return {
                    provider,
                    method,
                    name: account.holder_name,
                    email: account.zelle_email,
                };
            }
            if(method === 'INTERAC') {
                return {
                    provider,
                    method,
                    name: account.holder_name,
                    email: account.interac_email,
                };
            }
            if(method === 'FASTER' && provider === 'PLAID') {
                return {
                    provider,
                    method,
                    name: account.holder_name,
                    account_number: account.account,
                    sort_code: account.sort_code,
                };
            }
            if(method === 'FASTER' && provider === 'KLARNA') {
                return {
                    provider,
                    method,
                    name: account.holder_name,
                    account_number: account.account_number,
                    sort_code: account.bank_code,
                };
            }
            if(method === 'SEPA' && provider === 'KLARNA') {
                return {
                    provider,
                    method,
                    name: account.holder_name,
                    iban: account.iban,
                    bic: account.bic,
                };
            }
        }
    }
</script>

<style>
.w-400 {
    width: 400px;
}

.matcher-payment-provider-logo {
    height: 36px;
    background: white;
    margin-left: 1rem;
    border-radius: 4px;
}

</style>

<style scoped>
    #scroller {
        height: 50vh;
    }

    .avatar {
        border-radius: 50%;
        width: 100px;
        height: 100px;
    }

    .arrows {
        height: 105px;
    }

    @media screen and (max-width: 767px) {
        .avatar {
            width: 75px;
            height: 75px;
        }

        .arrows {
            height: 80px;
        }
    }

    .cancel {
        background-color: #FFEDD3;
    }
</style>
